import Phaser from 'phaser';
import {
  EXPLORE_LEVEL_ID,
  STAR_COLORS,
  defToProject,
  displayStars,
  findBackground,
  findBorder,
  hambiLabel,
  projectToLevel,
  projectPictureKey,
  verifiedLabel,
  type ExploreChapterRow,
  type ExploreRow,
} from '../../config/creator';
import { HUB_MENU_TRACK } from '../../config/levels';
import { setExploreLevel } from '../../config/testArena';
import { setExploreRun } from '../../config/exploreRun';
import { accountClient } from '../../account/accountUi';
import { exploreChapters, exploreGet, exploreList, explorePlay } from '../../account/explore';
import { ScrollList } from '../../ui/scrollList';
import { drawStarfield } from '../starfield';
import { getMusicService } from '../audio/MusicService';
import { creatorFlash, creatorHeader } from '../creator/creatorUi';

const ROW_W = 1080;
const ROW_H = 104;
const ROW_GAP = 12;
const LIST_X = 100;
const LIST_Y = 140;
const LIST_H = 560;
export const EXPLORE_VIOLET = 0x7b6cff;
export const INFO_BLUE = 0x2f7de1;
const HAMBI_ICON = 'tower-patty-1';
export const VERIFIED_GREEN = '#5bd35b';

export const EXPLORE_EMPTY = 'No levels yet — publish one from CREATE!';
export const EXPLORE_CHAPTERS_EMPTY = 'No chapters yet — make one in CREATE → CHAPTERS!';
export const EXPLORE_OFFLINE = 'Explore needs the online servers';

export type ExploreTab = 'levels' | 'chapters';

/**
 * Explore (spec 2026-11-02 §4.3, 2026-11-03): two tabs. LEVELS lists every
 * approved, live player level, most played first; CHAPTERS the published
 * chapters the same way. A level row: the code, the name, the maker's
 * username and the plays on the left; in its right quarter VERIFIED, the
 * stars (+ a fire when trending) and a blue info square over the Hambis
 * (difficulty) or N/A. Its border is the maker's chosen colour. Tap a level
 * to play it — a run that banks nothing but a rated level's first-win reward
 * — or a chapter to open it.
 */
export class ExploreScene extends Phaser.Scene {
  private list?: ScrollList;
  private status?: Phaser.GameObjects.Text;
  private busy = false;
  private tab: ExploreTab = 'levels';
  private loadSeq = 0;
  private tabButtons: {
    tab: ExploreTab;
    bg: Phaser.GameObjects.Rectangle;
    text: Phaser.GameObjects.Text;
  }[] = [];
  /** Rows as loaded (read by tests). */
  rows: ExploreRow[] = [];
  chapters: ExploreChapterRow[] = [];

  constructor() {
    super({ key: 'ExploreScene' });
  }

  init(data: { tab?: ExploreTab }): void {
    this.tab = data?.tab === 'chapters' ? 'chapters' : 'levels';
    this.sys.settings.data = {};
  }

  create(): void {
    this.busy = false;
    this.rows = [];
    this.chapters = [];
    this.tabButtons = [];
    drawStarfield(this, 1280, 720);
    creatorHeader(this, 'EXPLORE', () => this.scene.start('HubScene', { tab: 'explore' }));
    const music = getMusicService(this);
    music.playTrack(HUB_MENU_TRACK);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      music.stop();
      this.list?.destroy();
      this.list = undefined;
    });
    this.buildTabs();
    this.status = this.add
      .text(640, 400, 'Loading…', { fontSize: '26px', color: '#e8f4f1', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setName('explore-status');
    void this.loadData();
  }

  private buildTabs(): void {
    const tabs: [ExploreTab, string, number][] = [
      ['levels', 'LEVELS', 540],
      ['chapters', 'CHAPTERS', 740],
    ];
    for (const [tab, label, x] of tabs) {
      const bg = this.add
        .rectangle(x, 104, 180, 40, 0x15131f, 1)
        .setInteractive({ useHandCursor: true })
        .setName(`explore-tab-${tab}`);
      const text = this.add
        .text(x, 104, label, { fontSize: '18px', color: '#ffffff', fontStyle: 'bold' })
        .setOrigin(0.5)
        .setLetterSpacing(2);
      bg.on('pointerup', () => {
        if (this.tab === tab) return;
        this.tab = tab;
        this.restyleTabs();
        void this.loadData();
      });
      this.tabButtons.push({ tab, bg, text });
    }
    this.restyleTabs();
  }

  private restyleTabs(): void {
    for (const b of this.tabButtons) {
      const on = b.tab === this.tab;
      b.bg.setFillStyle(on ? EXPLORE_VIOLET : 0x15131f, 1).setStrokeStyle(2, EXPLORE_VIOLET, 1);
      b.text.setColor(on ? '#ffffff' : '#b9b0ff');
    }
  }

  private async loadData(): Promise<void> {
    const seq = ++this.loadSeq;
    this.list?.destroy();
    this.list = undefined;
    const client = accountClient();
    if (!client) {
      this.status?.setText(EXPLORE_OFFLINE);
      return;
    }
    this.status?.setText('Loading…').disableInteractive();
    const tab = this.tab;
    const r = tab === 'levels' ? await exploreList(client) : await exploreChapters(client);
    if (!this.sys.isActive() || seq !== this.loadSeq) return;
    if (!r.ok) {
      this.status
        ?.setText(`${r.error}\n\nTap to try again`)
        .setAlign('center')
        .setInteractive({ useHandCursor: true })
        .once('pointerup', () => void this.loadData());
      return;
    }
    if (tab === 'levels') {
      this.rows = r.value as ExploreRow[];
      this.status?.setText(this.rows.length === 0 ? EXPLORE_EMPTY : '');
    } else {
      this.chapters = r.value as ExploreChapterRow[];
      this.status?.setText(this.chapters.length === 0 ? EXPLORE_CHAPTERS_EMPTY : '');
    }
    this.render();
  }

  private render(): void {
    this.list?.destroy();
    const list = new ScrollList(this, {
      x: LIST_X,
      y: LIST_Y,
      width: ROW_W,
      height: LIST_H,
      barX: LIST_X + ROW_W + 18,
      depth: 1,
    });
    this.list = list;
    const n = this.tab === 'levels' ? this.rows.length : this.chapters.length;
    for (let i = 0; i < n; i++) {
      const y = i * (ROW_H + ROW_GAP) + ROW_H / 2;
      list.content.add(
        this.tab === 'levels'
          ? this.drawRow(this.rows[i], i, y, list)
          : this.drawChapterRow(this.chapters[i], i, y, list),
      );
    }
    list.setContentHeight(n * (ROW_H + ROW_GAP));
  }

  private drawRow(
    row: ExploreRow,
    i: number,
    y: number,
    list: ScrollList,
  ): Phaser.GameObjects.GameObject[] {
    const out: Phaser.GameObjects.GameObject[] = [];
    const cx = ROW_W / 2;
    const left = 0;
    const top = y - ROW_H / 2;
    const border = findBorder(row.border).color;
    const plate = this.add
      .rectangle(cx, y, ROW_W, ROW_H, 0x15131f, 0.85)
      .setStrokeStyle(2, border, 0.9)
      .setInteractive({ useHandCursor: true })
      .setName(`explore-row-${i}`);
    out.push(plate);
    // The level's own map, faint, like a Hub row's wash.
    const bg = findBackground(row.background);
    const pic = projectPictureKey(row);
    const washKey = pic && this.textures.exists(pic) ? pic : bg.key;
    if (washKey && this.textures.exists(washKey)) {
      const wash = this.add.image(cx, y, washKey).setAlpha(0.3);
      wash.setScale(Math.max(ROW_W / wash.width, ROW_H / wash.height));
      wash.setCrop(0, (wash.height - ROW_H / wash.scaleY) / 2, wash.width, ROW_H / wash.scaleY);
      out.push(wash);
    } else if (bg.color !== undefined) {
      out.push(this.add.rectangle(cx, y, ROW_W, ROW_H, bg.color, 0.25));
    }
    out.push(this.add.rectangle(left + 3, y, 6, ROW_H, border, 1));

    const textX = left + 28;
    out.push(
      this.add
        .text(textX, top + 16, `#${row.code}`, {
          fontSize: '13px',
          color: '#b9b0ff',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5),
      this.add
        .text(textX, top + 40, row.name, { fontSize: '26px', color: '#ffffff', fontStyle: 'bold' })
        .setOrigin(0, 0.5)
        .setName(`explore-name-${i}`),
      this.add
        .text(textX, top + 68, row.username, { fontSize: '16px', color: '#cfe0bd' })
        .setOrigin(0, 0.5)
        .setName(`explore-user-${i}`),
      this.add
        .text(textX, top + 88, `▶ ${row.plays} ${row.plays === 1 ? 'play' : 'plays'}`, {
          fontSize: '14px',
          color: '#9aa6ad',
        })
        .setOrigin(0, 0.5)
        .setName(`explore-plays-${i}`),
    );

    out.push(...this.ratings(row, i, y, list, 'explore'));

    plate.on('pointerover', () => plate.setStrokeStyle(3, 0xffffff, 1));
    plate.on('pointerout', () => plate.setStrokeStyle(2, border, 0.9));
    plate.on('pointerup', (ptr: Phaser.Input.Pointer) => {
      if (list.dragged || !list.inView(ptr)) return;
      void this.play(row);
    });
    return out;
  }

  /**
   * The right quarter of a row: VERIFIED to the left of the stars (+ fire), a
   * blue info square to their right (levels only), the Hambis under them.
   */
  private ratings(
    row: Pick<ExploreRow, 'plays' | 'starOverride' | 'hambis' | 'trending' | 'code'> & {
      verified?: number;
    },
    i: number,
    y: number,
    list: ScrollList,
    prefix: string,
  ): Phaser.GameObjects.GameObject[] {
    const out: Phaser.GameObjects.GameObject[] = [];
    const qx = ROW_W - ROW_W / 4 + 20;
    out.push(this.add.rectangle(qx - 20, y, 2, ROW_H - 24, 0xffffff, 0.15));
    const verified = verifiedLabel(row.verified ?? 0);
    if (verified) {
      out.push(
        this.add
          .text(qx - 34, y - 22, verified, {
            fontSize: '17px',
            color: VERIFIED_GREEN,
            fontStyle: 'bold',
          })
          .setOrigin(1, 0.5)
          .setName(`${prefix}-verified-${i}`),
      );
    }
    const stars = displayStars(row.plays, row.starOverride);
    const starText = this.add
      .text(qx, y - 22, '★'.repeat(stars.count) + '☆'.repeat(5 - stars.count), {
        fontSize: '26px',
        color: STAR_COLORS[stars.color],
      })
      .setOrigin(0, 0.5)
      .setName(`${prefix}-stars-${i}`);
    out.push(starText);
    if (row.trending && this.textures.exists('ui-fire')) {
      out.push(
        this.add
          .image(qx + starText.width + 18, y - 24, 'ui-fire')
          .setDisplaySize(30, 30)
          .setName(`${prefix}-fire-${i}`),
      );
    }
    if (prefix === 'explore') {
      // The information page (spec 2026-11-03 §3): a blue square, right of the stars.
      const ix = ROW_W - 34;
      const info = this.add
        .rectangle(ix, y - 22, 40, 40, INFO_BLUE, 1)
        .setStrokeStyle(2, 0xffffff, 0.7)
        .setInteractive({ useHandCursor: true })
        .setName(`explore-info-${i}`);
      const glyph = this.add
        .text(ix, y - 22, 'i', { fontSize: '26px', color: '#ffffff', fontStyle: 'bold' })
        .setOrigin(0.5);
      info.on(
        'pointerup',
        (ptr: Phaser.Input.Pointer, _x: number, _y: number, e: Phaser.Types.Input.EventData) => {
          e?.stopPropagation();
          if (list.dragged || !list.inView(ptr)) return;
          this.scene.start('ExploreInfoScene', { code: row.code });
        },
      );
      out.push(info, glyph);
    }
    const label = hambiLabel(row.hambis);
    if (row.hambis) {
      for (let h = 0; h < row.hambis; h++) {
        if (!this.textures.exists(HAMBI_ICON)) break;
        const icon = this.add.image(qx + 14 + h * 30, y + 24, HAMBI_ICON);
        icon.setScale(28 / Math.max(icon.width, icon.height));
        out.push(icon);
      }
    }
    out.push(
      this.add
        .text(row.hambis ? qx + 14 + row.hambis * 30 : qx, y + 24, label, {
          fontSize: '17px',
          color: row.hambis ? '#ffd23f' : '#9aa6ad',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5)
        .setName(`${prefix}-hambis-${i}`),
    );
    return out;
  }

  private drawChapterRow(
    row: ExploreChapterRow,
    i: number,
    y: number,
    list: ScrollList,
  ): Phaser.GameObjects.GameObject[] {
    const out: Phaser.GameObjects.GameObject[] = [];
    const cx = ROW_W / 2;
    const top = y - ROW_H / 2;
    const border = findBorder(row.border).color;
    const plate = this.add
      .rectangle(cx, y, ROW_W, ROW_H, 0x15131f, 0.9)
      .setStrokeStyle(4, border, 1)
      .setInteractive({ useHandCursor: true })
      .setName(`explore-chapter-${i}`);
    out.push(plate, this.add.rectangle(6, y, 12, ROW_H, border, 1));
    const textX = 32;
    out.push(
      this.add
        .text(textX, top + 16, `#${row.code}  ·  CHAPTER`, {
          fontSize: '13px',
          color: '#b9b0ff',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5),
      this.add
        .text(textX, top + 40, row.name, { fontSize: '26px', color: '#ffffff', fontStyle: 'bold' })
        .setOrigin(0, 0.5)
        .setName(`explore-chapter-name-${i}`),
      this.add
        .text(textX, top + 68, row.username, { fontSize: '16px', color: '#cfe0bd' })
        .setOrigin(0, 0.5),
      this.add
        .text(
          textX,
          top + 88,
          `${row.levelCount} ${row.levelCount === 1 ? 'level' : 'levels'}   ▶ ${row.plays} ${
            row.plays === 1 ? 'play' : 'plays'
          }`,
          { fontSize: '14px', color: '#9aa6ad' },
        )
        .setOrigin(0, 0.5)
        .setName(`explore-chapter-plays-${i}`),
    );
    out.push(...this.ratings(row, i, y, list, 'explore-chapter'));
    plate.on('pointerover', () => plate.setStrokeStyle(5, 0xffffff, 1));
    plate.on('pointerout', () => plate.setStrokeStyle(4, border, 1));
    plate.on('pointerup', (ptr: Phaser.Input.Pointer) => {
      if (list.dragged || !list.inView(ptr)) return;
      this.scene.start('ExploreChapterScene', { code: row.code });
    });
    return out;
  }

  /** Download the approved level, count the play, start the run. */
  async play(row: Pick<ExploreRow, 'code'>): Promise<void> {
    const client = accountClient();
    if (this.busy || !client) return;
    this.busy = true;
    const r = await exploreGet(client, row.code);
    if (!this.sys.isActive()) return;
    if (!r.ok) {
      this.busy = false;
      creatorFlash(this, r.error);
      return;
    }
    if (!startExploreRun(this, r.value)) {
      this.busy = false;
      creatorFlash(this, "This level can't be played");
      return;
    }
    // The server never counts the creator's own play.
    void explorePlay(client, r.value.code);
  }
}

/**
 * Start a downloaded level: register it under EXPLORE_LEVEL_ID, remember the
 * run (its code, its difficulty, the chapter it came from) and go. False when
 * the level has no wave or plot.
 */
export function startExploreRun(
  scene: Phaser.Scene,
  level: { code: string; name: string; def: Record<string, unknown>; hambis: number | null },
  chapterCode?: string,
): boolean {
  const def = projectToLevel(defToProject(level.name, level.def), EXPLORE_LEVEL_ID);
  if (def.waves.length === 0 || def.plots.length === 0) return false;
  setExploreLevel(def);
  setExploreRun({
    code: level.code,
    hambis: level.hambis,
    ...(chapterCode ? { chapter: { code: chapterCode } } : {}),
  });
  scene.scene.start('GameScene', { levelId: EXPLORE_LEVEL_ID });
  scene.scene.launch('HudScene', { levelId: EXPLORE_LEVEL_ID });
  return true;
}
